import React, { useState } from "react";
import {
  Container,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate, useParams } from "react-router-dom";
import envConfig from "../../config";
import { getHeaders } from "../../utils/api";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { getAppTheme } from "../../config/resolve-app-brand";

const InviteSubscriberPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { isDarkMode } = useTheme();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const theme = getAppTheme(isDarkMode);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    loginEmail: "",
    allowAddNewBusinessFiles: false,
    allowManageOtherUsers: false,
    allowSTPLodgement: false,
    termsAccepted: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: event.target.checked,
    }));
  };

  const handleInputChange = (field) => (event) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: event.target.value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    }
    if (!formData.loginEmail.trim()) {
      newErrors.loginEmail = "Login Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.loginEmail)) {
      newErrors.loginEmail = "Invalid email address";
    }
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = "This field is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInvite = async () => {
    if (!validateForm()) {
      return;
    }
    if (!executeRecaptcha) return;
    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha("invite_subscriber");
    } catch {
      enqueueSnackbar("reCAPTCHA verification failed. Please try again.", { variant: "error" });
      return;
    }

    setLoading(true);

    const payload = {
      FirstName: formData.firstName,
      LastName: formData.lastName,
      LoginEmail: formData.loginEmail,
      AllowAddNewBusinessFilesYN: formData.allowAddNewBusinessFiles ? "Y" : "N",
      AllowManageOtherUsersYN: formData.allowManageOtherUsers ? "Y" : "N",
      AllowSTPLodgementYN: formData.allowSTPLodgement ? "Y" : "N",
      SubscriberId: parseInt(id),
    };

    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v4/en-au/subscribers/invite-new-subscriber-member`,
        {
          method: "POST",
          headers: {
            ...getHeaders(),
            captchav3: envConfig.loginUrl + ":" + recaptchaToken,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (data.SuccessYN === "Y") {
        enqueueSnackbar("User invited successfully!", { variant: "success" });
        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          loginEmail: "",
          allowAddNewBusinessFiles: false,
          allowManageOtherUsers: false,
          allowSTPLodgement: false,
          termsAccepted: false,
        });
      } else {
        // Error handled by interceptor
      }
    } catch (error) {
      console.error("Error inviting subscriber member:", error);
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/subscriber/${id}`);
  };

  return (
    <Container maxWidth="sm" sx={{ mb: 3 }}>
      <Typography variant="h4" gutterBottom>
        New Subscriber Member
      </Typography>
      <Box component="form" noValidate autoComplete="off">
        <TextField
          required
          fullWidth
          label="First Name"
          value={formData.firstName}
          onChange={handleInputChange("firstName")}
          margin="normal"
          error={!!errors.firstName}
          helperText={errors.firstName}
          data-testid="invite-subscriber-page--TextField-0" />
        <TextField
          required
          fullWidth
          label="Last Name"
          value={formData.lastName}
          onChange={handleInputChange("lastName")}
          margin="normal"
          error={!!errors.lastName}
          helperText={errors.lastName}
          data-testid="invite-subscriber-page--TextField-1" />
        <TextField
          required
          fullWidth
          label="Login Email"
          value={formData.loginEmail}
          onChange={handleInputChange("loginEmail")}
          margin="normal"
          error={!!errors.loginEmail}
          helperText={errors.loginEmail}
          data-testid="invite-subscriber-page--TextField-2" />

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.allowAddNewBusinessFiles}
                onChange={handleCheckboxChange("allowAddNewBusinessFiles")}
                data-testid="invite-subscriber-page--Checkbox-0" />
            }
            label="Allow to add new Business Files"
            data-testid="invite-subscriber-page--FormControlLabel-0" />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.allowManageOtherUsers}
                onChange={handleCheckboxChange("allowManageOtherUsers")}
                data-testid="invite-subscriber-page--Checkbox-1" />
            }
            label="Allow to manage other members"
            data-testid="invite-subscriber-page--FormControlLabel-1" />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.allowSTPLodgement}
                onChange={handleCheckboxChange("allowSTPLodgement")}
                data-testid="invite-subscriber-page--Checkbox-allow-stp-lodgement" />
            }
            label="Allow lodging STP as Tax Agent"
            data-testid="invite-subscriber-page--FormControlLabel-allow-stp-lodgement" />
        </Box>

        {/* Terms and Conditions */}
        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.termsAccepted}
                onChange={handleCheckboxChange("termsAccepted")}
                data-testid="invite-subscriber-page--Checkbox-2" />
            }
            label={
              <Typography>
                I am authorised to provide these details according to{" "}
                <a
                  className="link"
                  href="https://exacc.au/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    textTransform: "capitalize",
                    textDecoration: "none",
                  }}
                  data-testid="invite-subscriber-page--a-0">
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a
                  href="https://exacc.au/privacy-policy"
                  className="link"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    textTransform: "capitalize",
                    textDecoration: "none",
                  }}
                  data-testid="invite-subscriber-page--a-1">
                  Privacy Policy
                </a>
                <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
            }
            data-testid="invite-subscriber-page--FormControlLabel-2" />
          {errors.termsAccepted && (
            <Typography color="error" variant="caption">
              {errors.termsAccepted}
            </Typography>
          )}
        </Box>

        <Grid container spacing={2} mt={2} sx={{ pb: 3 }}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleInvite}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
              data-testid="invite-subscriber-page--Button-0">
              Invite
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              data-testid="invite-subscriber-page--Button-1">
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default InviteSubscriberPage;
